import {
  ResponseTimeOperator,
  ResponseType,
} from "@redotech/redo-flows-common/rules-v3/schemas";
import { TimeUnit } from "@redotech/redo-flows-common/rules-v3/types";
import { AssignmentTypeOption } from "@redotech/redo-flows-common/rules/schemas";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { WaitTimeUnit } from "@redotech/redo-flows-common/steps";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { DefaultRule } from "../types";

/** Closes an AI ticket 2 days after an AI reply if the customer hasn't responded.
 * On by default; hidden so merchants can't disable this billing-critical rule. */
export const closeInactiveAiConversationRule = {
  id: "default-close-inactive-ai-conversation",
  name: "Close inactive AI conversations",
  description:
    "Close a ticket 2 days after an AI-assigned reply if the customer has not responded.",
  hidden: true,
  triggerType: RuleTriggerType.MERCHANT_MESSAGE_CREATED,

  defaultConfig: {
    defaultRuleId: "default-close-inactive-ai-conversation",
    enabled: true,
    sortIndex: 11,
    configurations: {},
  },

  template: {
    name: "Close inactive AI conversations",
    description:
      "Close a ticket 2 days after an AI-assigned reply if the customer has not responded.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.MERCHANT_MESSAGE_CREATED,
        nextId: "ai-assigned-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "ai-assigned-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.AI_AGENT_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.MESSAGE_TYPE,
              messageTypes: ["internalNote"],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "wait",
      },
      {
        type: StepType.WAIT,
        id: "wait",
        numDays: 2,
        timeUnit: WaitTimeUnit.DAYS,
        nextId: "still-ai-and-quiet-check",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "still-ai-and-quiet-check",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.ASSIGNMENT,
              mode: {
                _tag: "type",
                assignmentTypes: [AssignmentTypeOption.AI_AGENT_ASSIGNEE],
              },
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: ["OPEN"],
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.RESPONSE_TIME,
              responseType: ResponseType.CUSTOMER,
              responseTimeOperator: ResponseTimeOperator.MORE_THAN,
              value: 2,
              unit: TimeUnit.DAYS,
            },
          ],
        },
        nextTrueId: "close",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "close",
        actions: [
          { actionType: RuleActionType.SET_STATUS_V3, status: "CLOSED" },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule;
